<template>
  <div class="admin-container">
    <div class="admin-header">
      <h1>Party Booking Management</h1>
      <div class="auth-section" v-if="!isAuthenticated">
        <input type="password" v-model="password" placeholder="Enter admin password" @keyup.enter="authenticate" />
        <button @click="authenticate">Login</button>
      </div>
    </div>

    <div v-if="isAuthenticated" class="admin-content">
      <div class="filters">
        <div class="filter-group">
          <label>Status:</label>
          <select v-model="statusFilter">
            <option value="ALL">All</option>
            <option value="PENDING">Pending</option>
            <option value="CONFIRMED">Confirmed</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>

        <div class="filter-group">
          <label>Date Range:</label>
          <div class="date-range">
            <input type="date" v-model="startDate" />
            <span>to</span>
            <input type="date" v-model="endDate" />
          </div>
        </div>

        <button class="filter-btn" @click="applyFilters">Apply Filters</button>
        <button class="reset-btn" @click="resetFilters">Reset</button>
      </div>

      <div class="bookings-table-container">
        <table class="bookings-table">
          <thead>
            <tr>
              <th>Booking #</th>
              <th>Parent Name</th>
              <th>Child Name</th>
              <th>Package</th>
              <th>Date</th>
              <th>Time</th>
              <th>Party Size</th>
              <th>Deposit</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="booking in bookings" :key="booking.id"
              :class="{ 'pending': booking.bookingStatus === 'PENDING' }">
              <td>{{ booking.bookingNumber }}</td>
              <td>{{ booking.parentName }}</td>
              <td>{{ booking.childName }}</td>
              <td>{{ booking.packageType }}</td>
              <td>{{ formatDate(booking.bookingDate) }}</td>
              <td>{{ booking.timeSlot }}</td>
              <td>{{ booking.partySize }}</td>
              <td>
                <span :class="['status-badge', booking.depositPaid ? 'paid' : 'unpaid']">
                  {{ booking.depositPaid ? 'Paid' : 'Unpaid' }}
                </span>
              </td>
              <td>
                <span :class="['status-badge', getStatusClass(booking.bookingStatus)]">
                  {{ getStatusText(booking.bookingStatus) }}
                </span>
              </td>
              <td>
                <button class="view-btn" @click="viewBookingDetails(booking)">View</button>
                <button class="status-btn" @click="updateStatus(booking, 'CONFIRMED')"
                  v-if="booking.bookingStatus === 'PENDING'">Confirm</button>
                <button class="status-btn cancel" @click="updateStatus(booking, 'CANCELLED')"
                  v-if="booking.bookingStatus !== 'CANCELLED'">Cancel</button>
                <button class="delete-btn" 
                  @click="deleteBooking(booking)"
                  v-if="booking.bookingStatus === 'CANCELLED'">
                  Delete
                </button>
              </td>
            </tr>
            <tr v-if="bookings.length === 0">
              <td colspan="10" class="no-data">No booking data available</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination controls -->
      <div class="pagination">
        <button :disabled="currentPage === 1" @click="changePage(currentPage - 1)">Previous</button>
        <span>Page {{ currentPage }} of {{ totalPages }}</span>
        <button :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">Next</button>
      </div>
    </div>

    <!-- Booking details modal -->
    <div v-if="showDetailModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div id="pdfContent">
          <div class="modal-header">
            <h2>Booking Details</h2>
            <button @click="exportPdf">Import as PDF</button>
            <button class="close-btn" @click="closeModal">&times;</button>
          </div>
          <div class="modal-body" v-if="selectedBooking">
            <div class="detail-section">
              <h3>Personal Information</h3>
              <div class="detail-row">
                <div class="detail-item">
                  <span class="label">Parent Name:</span>
                  <span class="value">{{ selectedBooking.parentName }}</span>
                </div>
                <div class="detail-item">
                  <span class="label">Email:</span>
                  <span class="value">{{ selectedBooking.email }}</span>
                </div>
              </div>
              <div class="detail-row">
                <div class="detail-item">
                  <span class="label">Phone:</span>
                  <span class="value">{{ selectedBooking.phone }}</span>
                </div>
                <div class="detail-item">
                  <span class="label">Child's Name:</span>
                  <span class="value">{{ selectedBooking.childName }}</span>
                </div>
              </div>
              <div class="detail-row">
                <div class="detail-item">
                  <span class="label">Child's Age:</span>
                  <span class="value">{{ selectedBooking.childAge }} years</span>
                </div>
              </div>
            </div>

            <div class="detail-section">
              <h3>Party Details</h3>
              <div class="detail-row">
                <div class="detail-item">
                  <span class="label">Package:</span>
                  <span class="value">{{ selectedBooking.packageType }}</span>
                </div>
                <div class="detail-item">
                  <span class="label">Party Size:</span>
                  <span class="value">{{ selectedBooking.partySize }} guests</span>
                </div>
              </div>
              <div class="detail-row">
                <div class="detail-item">
                  <span class="label">Themes:</span>
                  <span class="value">{{ selectedBooking.themes }}</span>
                </div>
              </div>
              <div class="detail-row">
                <div class="detail-item">
                  <span class="label">Date:</span>
                  <span class="value">{{ formatDate(selectedBooking.bookingDate) }}</span>
                </div>
                <div class="detail-item">
                  <span class="label">Time:</span>
                  <span class="value">{{ selectedBooking.timeSlot }}</span>
                </div>
              </div>
            </div>

            <div class="detail-section">
              <h3>Food Selection</h3>
              <div v-if="!selectedBooking.selectedFoodItems || selectedBooking.selectedFoodItems === '[]'" class="no-food">
                No food items selected
              </div>
              <div v-else>
                <div class="food-items">
                  <div v-for="(item, index) in parsedFoodItems" :key="index" class="food-item">
                    <span class="food-name">{{ item.name }}</span>
                    <span class="food-quantity">x{{ item.quantity }}</span>
                    <span class="food-price">${{ item.price * item.quantity }}</span>
                  </div>
                </div>
                <div class="food-total">
                  <strong>Food Total: ${{ calculateFoodTotal }}</strong>
                </div>
              </div>

              <div class="food-notes" v-if="selectedBooking.foodNotes">
                <span class="label">Food Notes:</span>
                <p>{{ selectedBooking.foodNotes }}</p>
              </div>

              <div class="coffee-tab" v-if="selectedBooking.coffeeTab">
                <span class="label">Coffee Option:</span>
                <span class="value">Coffee service selected</span>
              </div>
            </div>

            <div class="detail-section">
              <h3>Payment Information</h3>
              <div class="detail-row">
                <div class="detail-item">
                  <span class="label">Package Price:</span>
                  <span class="value">${{ selectedBooking.packagePrice }}</span>
                </div>
                <div class="detail-item">
                  <span class="label">Selected Food Total:</span>
                  <span class="value">${{ selectedBooking.foodTotal }}</span>
                </div>
              </div>
              <div class="detail-row">
                <div class="detail-item">
                  <span class="label">Additional Food Costs:</span>
                  <span class="value">${{ calculateAdditionalFoodCosts }}</span>
                </div>
                <div class="detail-item">
                  <span class="label">Deposit Amount(50% Package Price):</span>
                  <span class="value">${{ selectedBooking.depositAmount }}</span>
                </div>
              </div>
              <div class="detail-row">
                <div class="detail-item">
                  <span class="label">Deposit Status:</span>
                  <span :class="['value', 'status-badge', selectedBooking.depositPaid ? 'paid' : 'unpaid']">
                    {{ selectedBooking.depositPaid ? 'Paid' : 'Unpaid' }}
                  </span>
                </div>
                <div class="detail-item">
                  <span class="label">Booking Status:</span>
                  <span :class="['value', 'status-badge', getStatusClass(selectedBooking.bookingStatus)]">
                    {{ getStatusText(selectedBooking.bookingStatus) }}
                  </span>
                </div>
              </div>
            </div>

            <div class="detail-section">
              <h3>Booking Timeline</h3>
              <div class="detail-row">
                <div class="detail-item">
                  <span class="label">Created:</span>
                  <span class="value">{{ formatDateTime(selectedBooking.createdAt) }}</span>
                </div>
                <div class="detail-item">
                  <span class="label">Last Updated:</span>
                  <span class="value">{{ formatDateTime(selectedBooking.updatedAt) }}</span>
                </div>
              </div>
            </div>

            <div class="detail-section">
              <h3>Final Notes</h3>
              <div class="detail-row">
                <div class="detail-item">
                  <span class="label">Additional Notes:</span>
                  <p class="value notes-content">{{ selectedBooking.finalNotes || 'No additional notes' }}</p>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <div class="status-update">
              <label>Update Status:</label>
              <select v-model="newStatus">
                <option value="PENDING">Pending</option>
                <option value="CONFIRMED">Confirmed</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
              <button class="update-btn" @click="saveStatusUpdate">Save</button>
            </div>
            <div class="deposit-update">
              <label>Deposit Status:</label>
              <select v-model="newDepositStatus">
                <option :value="true">Paid</option>
                <option :value="false">Unpaid</option>
              </select>
              <button class="update-btn" @click="saveDepositUpdate">Save</button>
            </div>
            <button class="close-btn-secondary" @click="closeModal">Close</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import html2pdf from 'html2pdf.js'
import { ElMessage, ElMessageBox } from 'element-plus';

export default {
  name: 'AdminBookingView',
  setup() {
    // 认证状态
    const isAuthenticated = ref(false);
    const password = ref('');

    // 预约数据
    const bookings = ref([]);
    const selectedBooking = ref(null);
    const showDetailModal = ref(false);

    // 分页
    const currentPage = ref(1);
    const pageSize = ref(10);
    const totalPages = ref(1);
    const totalItems = ref(0);

    // 筛选
    const statusFilter = ref('ALL');
    const startDate = ref('');
    const endDate = ref('');

    // 状态更新
    const newStatus = ref('');
    const newDepositStatus = ref(false);

    // 解析食物项目
    const parsedFoodItems = computed(() => {
      if (!selectedBooking.value?.selectedFoodItems) return [];
      try {
        return JSON.parse(selectedBooking.value.selectedFoodItems);
      } catch (e) {
        console.error('Error parsing food items:', e);
        return [];
      }
    });

    const calculateFoodTotal = computed(() => {
      return parsedFoodItems.value.reduce((total, item) => {
        return total + (item.price * item.quantity);
      }, 0);
    });

    const calculateAdditionalFoodCosts = computed(() => {
      if (!selectedBooking.value) return 0;
      
      const packageType = selectedBooking.value.packageType;
      const selectedFoodTotal = selectedBooking.value.foodTotal || 0;
      
      // 根据套餐类型设置食物额度
      let foodCredit = 0;
      if (packageType === 'Climber' || packageType === 'Explorer') {
        foodCredit = 200;
      } else if (packageType === 'Investigator' || packageType === 'Inventor') {
        foodCredit = 400;
      } else if (packageType === 'Private') {
        foodCredit = 600;
      }
      
      // 计算额外费用：食物总价 - 食物额度
      const additionalCost = selectedFoodTotal - foodCredit;
      return additionalCost > 0 ? additionalCost : 0;
    });

    // 认证方法
    const authenticate = () => {
      // 简化认证，使用固定密码
      if (password.value === 'admin123') {
        isAuthenticated.value = true;
        localStorage.setItem('isAuthenticated', 'true');
        fetchBookings(); // 加载模拟数据
      } else {
        alert('Incorrect password');
      }
    };

    // 获取预约数据
    const fetchBookings = async () => {
      try {
        const response = await fetch('/api/admin/bookings');
        if (!response.ok) {
          throw new Error('Failed to fetch bookings');
        }
        const data = await response.json();
        console.log(data);
        
        // 按创建时间倒序排序
        bookings.value = data.bookings.sort((a, b) => {
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
        
        // 计算分页
        totalItems.value = bookings.value.length;
        totalPages.value = Math.ceil(totalItems.value / pageSize.value);

        // 应用本地分页
        const startIndex = (currentPage.value - 1) * pageSize.value;
        const endIndex = startIndex + pageSize.value;
        bookings.value = bookings.value.slice(startIndex, endIndex);
      } catch (error) {
        console.error('Error:', error);
        ElMessage.error('Failed to fetch bookings');
      }
    };

    // 查看预约详情
    const viewBookingDetails = (booking) => {
      selectedBooking.value = { ...booking }; // 复制一份，避免直接修改原数据
      newStatus.value = booking.bookingStatus;
      newDepositStatus.value = booking.depositPaid;
      showDetailModal.value = true;
    };

    // 更新预约状态
    const updateStatus = async (booking, status) => {
      try {
        const response = await fetch(`/api/admin/bookings/${booking.id}/status`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status })
        });

        if (!response.ok) {
          throw new Error('Failed to update booking status');
        }

        // 重新获取数据
        await fetchBookings();
      } catch (error) {
        console.error('Error updating booking status:', error);
      }
    };

    // 保存状态更新
    const saveStatusUpdate = async () => {
      if (!selectedBooking.value) return;

      try {
        const response = await fetch(`/api/admin/bookings/${selectedBooking.value.id}/status`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status: newStatus.value })
        });

        if (!response.ok) {
          throw new Error('Failed to update booking status');
        }

        closeModal();
        await fetchBookings();
      } catch (error) {
        console.error('Error updating booking status:', error);
      }
    };

    // 保存定金状态更新
    const saveDepositUpdate = async () => {
      if (!selectedBooking.value) return;

      try {
        const response = await fetch(`/api/admin/bookings/${selectedBooking.value.id}/deposit`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ depositPaid: newDepositStatus.value })
        });

        if (!response.ok) {
          throw new Error('Failed to update deposit status');
        }

        closeModal();
        await fetchBookings();
      } catch (error) {
        console.error('Error updating deposit status:', error);
      }
    };

    // 关闭弹窗
    const closeModal = () => {
      showDetailModal.value = false;
      selectedBooking.value = null;
    };

    // 分页
    const changePage = (page) => {
      currentPage.value = page;
      fetchBookings();
    };

    // 应用筛选
    const applyFilters = () => {
      currentPage.value = 1;
      fetchBookings();
    };

    // 重置筛选
    const resetFilters = () => {
      statusFilter.value = 'ALL';
      startDate.value = '';
      endDate.value = '';
      currentPage.value = 1;
      fetchBookings();
    };

    // 格式化日期
    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long'
      });
    };

    // 格式化日期时间
    const formatDateTime = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleString('en-US');
    };

    // 获取状态文本
    const getStatusText = (status) => {
      switch (status) {
        case 'PENDING': return 'Pending';
        case 'CONFIRMED': return 'Confirmed';
        case 'CANCELLED': return 'Cancelled';
        default: return status;
      }
    };

    // 获取状态类名
    const getStatusClass = (status) => {
      switch (status) {
        case 'PENDING': return 'pending';
        case 'CONFIRMED': return 'confirmed';
        case 'CANCELLED': return 'cancelled';
        default: return '';
      }
    };
    // 导出 PDF
    const exportPdf = () => {
      const element = document.getElementById('pdfContent')
      const options = {
        margin: 0.5,
        filename: 'booking-details.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
      }
      html2pdf().from(element).set(options).save()
    }

    // 删除预订
    const deleteBooking = async (booking) => {
      try {
        // 确认对话框
        const confirmed = await ElMessageBox.confirm(
          `Are you sure you want to delete booking #${booking.bookingNumber}?`,
          'Warning',
          {
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            type: 'warning',
          }
        );

        if (confirmed) {
          const response = await fetch(`/api/admin/bookings/${booking.bookingNumber}`, {
            headers: {
              'Content-Type': 'application/json'
            },
            method: 'DELETE',
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Failed to delete booking');
          }

          ElMessage({
            type: 'success',
            message: 'Booking deleted successfully',
            duration: 3000
          });

          // 重新获取预订列表
          await fetchBookings();
        }
      } catch (error) {
        console.error('Error:', error);
        ElMessage({
          type: 'error',
          message: error.message || 'Failed to delete booking',
          duration: 5000
        });
      }
    };

    onMounted(() => {
      if (localStorage.getItem('isAuthenticated') === 'true') {
        isAuthenticated.value = true;
        fetchBookings();
      }// 初始化逻辑，如果需要的话
    });

    return {
      isAuthenticated,
      password,
      bookings,
      selectedBooking,
      showDetailModal,
      currentPage,
      totalPages,
      statusFilter,
      startDate,
      endDate,
      newStatus,
      newDepositStatus,
      parsedFoodItems,
      calculateFoodTotal,
      calculateAdditionalFoodCosts,
      authenticate,
      fetchBookings,
      viewBookingDetails,
      updateStatus,
      saveStatusUpdate,
      saveDepositUpdate,
      closeModal,
      changePage,
      applyFilters,
      resetFilters,
      formatDate,
      formatDateTime,
      getStatusText,
      getStatusClass,
      exportPdf,
      deleteBooking
    };
  }
}
</script>

<style scoped>
.admin-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  padding-top: 120px;
}

.admin-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.admin-header h1 {
  color: #2c3e50;
}

.auth-section {
  display: flex;
  gap: 1rem;
}

.auth-section input {
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.auth-section button {
  padding: 0.5rem 1rem;
  background-color: #42b983;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: #f9f9f9;
  border-radius: 8px;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.date-range {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.filter-btn,
.reset-btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.filter-btn {
  background-color: #42b983;
  color: white;
}

.reset-btn {
  background-color: #f5f5f5;
  color: #666;
}

.bookings-table-container {
  overflow-x: auto;
  margin-bottom: 1.5rem;
}

.bookings-table {
  width: 100%;
  border-collapse: collapse;
}

.bookings-table th,
.bookings-table td {
  padding: 0.8rem;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.bookings-table th {
  background-color: #f5f5f5;
  font-weight: 500;
}

.bookings-table tr:hover {
  background-color: #f9f9f9;
}

.bookings-table tr.pending {
  background-color: #fff8e1;
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.85em;
}

.status-badge.pending {
  background-color: #fff8e1;
  color: #f57c00;
}

.status-badge.confirmed {
  background-color: #e8f5e9;
  color: #388e3c;
}

.status-badge.cancelled {
  background-color: #ffebee;
  color: #d32f2f;
}

.status-badge.paid {
  background-color: #e8f5e9;
  color: #388e3c;
}

.status-badge.unpaid {
  background-color: #ffebee;
  color: #d32f2f;
}

.view-btn,
.status-btn {
  padding: 0.25rem 0.5rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 0.5rem;
  font-size: 0.85em;
}

.view-btn {
  background-color: #e3f2fd;
  color: #1976d2;
}

.status-btn {
  background-color: #e8f5e9;
  color: #388e3c;
}

.status-btn.cancel {
  background-color: #ffebee;
  color: #d32f2f;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
}

.pagination button {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  background-color: #f5f5f5;
  cursor: pointer;
}

.pagination button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.no-data {
  text-align: center;
  padding: 2rem;
  color: #666;
  font-style: italic;
}

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #eee;
}

.modal-header h2 {
  margin: 0;
  color: #2c3e50;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
}

.modal-body {
  padding: 1.5rem;
}

.detail-section {
  margin-bottom: 2rem;
}

.detail-section h3 {
  margin-top: 0;
  margin-bottom: 1rem;
  color: #2c3e50;
  border-bottom: 1px solid #eee;
  padding-bottom: 0.5rem;
}

.detail-row {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-bottom: 1rem;
}

.detail-item {
  flex: 1;
  min-width: 250px;
}

.label {
  font-weight: 500;
  color: #666;
}

.food-items {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.food-item {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 1rem;
  align-items: center;
  padding: 0.8rem;
  background-color: #f9f9f9;
  border-radius: 4px;
}

.food-quantity {
  color: #666;
  font-weight: 500;
}

.food-price {
  font-weight: 500;
  color: #42b983;
  text-align: right;
  min-width: 80px;
}

.food-total {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px dashed #ddd;
  text-align: right;
  font-size: 1.1em;
}

.no-food {
  padding: 1rem;
  text-align: center;
  color: #666;
  font-style: italic;
}

.food-notes {
  margin-top: 1rem;
  padding: 1rem;
  background-color: #f9f9f9;
  border-radius: 4px;
}

.coffee-tab {
  margin-top: 1rem;
  padding: 0.5rem;
  background-color: #e8f5e9;
  border-radius: 4px;
  display: inline-block;
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #eee;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: space-between;
  align-items: center;
}

.status-update,
.deposit-update {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.update-btn {
  padding: 0.5rem 1rem;
  background-color: #42b983;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.close-btn-secondary {
  padding: 0.5rem 1rem;
  background-color: #f5f5f5;
  color: #666;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.notes-content {
  white-space: pre-wrap;
  /* 保留换行符 */
  background-color: #f9f9f9;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 0.5rem;
  font-style: italic;
  color: #666;
}

.detail-item .value {
  margin-top: 0.5rem;
  line-height: 1.5;
}

@media (max-width: 768px) {
  .admin-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .filters {
    flex-direction: column;
  }

  .modal-footer {
    flex-direction: column;
    align-items: flex-start;
  }
}

/* 添加删除按钮样式 */
.delete-btn {
  padding: 0.3rem 0.8rem;
  background-color: #ff4949;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-left: 0.5rem;
  font-size: 0.9em;
}

.delete-btn:hover {
  background-color: #f56c6c;
}
</style>